<template>
  <div>
    <transition name="slide-fade">
      <div v-if="mqttStatus !== 'connected'" class="mqtt-banner" :class="mqttStatus">
        <span class="status-dot" :class="mqttStatus"></span>
        <v-icon small class="banner-icon" :class="{ 'spin': mqttStatus === 'reconnecting' }">
          {{ mqttStatus === 'reconnecting' ? 'sync' : mqttStatus === 'offline' ? 'wifi_off' : 'access_time' }}
        </v-icon>
        <span class="banner-text">
          {{ mqttStatus === 'reconnecting' ? 'Reconnecting to live feed…' : mqttStatus === 'offline' ? 'Live feed disconnected' : 'Data may be outdated' }}
        </span>
      </div>
    </transition>
    <div v-if="mqttStatus === 'connected'" class="live-indicator">
      <span class="live-dot"></span>
      <span class="live-text">Live</span>
    </div>

    <div class="legend-container">
      <MapLegend
        v-for="leg in legend"
        :key="leg.name"
        class="px-2 py-1"
        :text="$t(leg.name)"
        :shape="leg.attr"
      />
    </div>  
    <div class="container-height">
      <StatusCard :blockList="blockList" :groupedByBlock="groupedByBlock" :healthyValues="healthyValues" :paramNameMap="paramNameMap" :deviceConnected="deviceConnected" :loggerDisconnected="loggerDisconnected"/>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapMutations } from 'vuex'
import fireAlarmApi from '../services/firealarm'
import { mapGettersMixin } from '@/util/mixin/index'
import { mqttStatusMixin } from '@/util/mixin/mqttStatusMixin'
import StatusCard from '../components/common/StatusCards.vue'
import MapLegend from '../components/common/MapLegend.vue'

export default {
  mixins: [ mapGettersMixin, mqttStatusMixin ],
  components: {
    StatusCard,
    MapLegend
  },
  data() {
    return {
      blockList: [],
      fireAlarmData: {},
      healthyValues: {},
      paramNameMap: {},
      deviceConnected: {},
      loggerDisconnected: {},
      apiDataLoaded: false,
    }
  },
  created() {
    this.initialStates()
  },
  computed: {
    ...mapGetters(['getSelectedPlantId', 'getGeneralState']),
    groupedByBlock() {
      const groupedObject = {}
      Object.keys(this.fireAlarmData).forEach(key => {
        const block = key.split('-')[0]
        if (!groupedObject[block]) {
          groupedObject[block] = {};
        }
        groupedObject[block][key] = this.fireAlarmData[key]
      })
      return groupedObject
    }
  },
  methods: {
    ...mapMutations([ 'SET_PROGRESS_BAR' ]),
    initialStates() {
      this.unsubscribeMqtt()
      this.resetMqttState()
      this.deviceConnected = {}
      this.loggerDisconnected = {}
      this.callAllApi()
    },
    async callAllApi() {
      await this.getBlockList()
      await this.getFireAlarmData()
      this.apiDataLoaded = true
      this.subscribeMqtt()
    },
    async getFireAlarmData() {
      try {
        this.SET_PROGRESS_BAR(true)
        const response = await fireAlarmApi.getFireAlarmData(this.getSelectedPlantId)
        this.fireAlarmData = response.data.data || {}
        this.healthyValues = response.data.config || {}
        this.paramNameMap = response.data.name_map || {}
        Object.keys(this.fireAlarmData).forEach(deviceId => {
          this.$set(this.deviceConnected, deviceId, true)
          this._scheduleDeviceStaleTimer(deviceId)
        })
      } catch (error) {
        console.error('[FireAlarm] Failed to fetch fire alarm data:', error)
        this.SET_PROGRESS_BAR(false)
      } finally {
        if(Object.keys(this.groupedByBlock).length > 0) {
          this.SET_PROGRESS_BAR(false) 
        }
      }
    },
    async getBlockList() {
      try {
        this.SET_PROGRESS_BAR(true)
        const response = await fireAlarmApi.getBlockList(this.getSelectedPlantId)
        this.blockList = response.data.message
      } catch(err) {
        console.error('[FireAlarm] Failed to fetch block list:', err)
        this.SET_PROGRESS_BAR(false)
      } finally {
        this.SET_PROGRESS_BAR(false)
      }
    },
    subscribeMqtt() {
      const plantId = this.getSelectedPlantId
      if (!plantId) return
      try {
        this.$mqtt.subscribe(`live/${plantId}/fire alarm`)
        this.bindMqttEvents()
        this.startMqttStaleTimer()
      } catch (e) {
        console.error('[FireAlarm] MQTT subscribe error:', e)
      }
    },
    unsubscribeMqtt(plantId = this.getSelectedPlantId) {
      if (!plantId) return
      try {
        this.$mqtt.unsubscribe(`live/${plantId}/fire alarm`)
      } catch (e) {
        console.error('[FireAlarm] MQTT unsubscribe error:', e)
      }
    },
    _scheduleDeviceStaleTimer(deviceId) {
      this._scheduleDeviceStaleTimerMixin(deviceId, (expiredDeviceId) => {
        if (this.paramNameMap[expiredDeviceId]) {
          Object.keys(this.paramNameMap[expiredDeviceId]).forEach(key => {
            if (key in this.fireAlarmData[expiredDeviceId]) {
              this.$set(this.fireAlarmData[expiredDeviceId], key, null)
            }
          })
        }
        this.$set(this.deviceConnected, expiredDeviceId, false)
      })
    },
    _markDeviceLive(deviceId) {
      this._markDeviceLiveMixin(deviceId, (expiredDeviceId) => {
        if (this.paramNameMap[expiredDeviceId]) {
          Object.keys(this.paramNameMap[expiredDeviceId]).forEach(key => {
            if (key in this.fireAlarmData[expiredDeviceId]) {
              this.$set(this.fireAlarmData[expiredDeviceId], key, null)
            }
          })
        }
        this.$set(this.deviceConnected, expiredDeviceId, false)
      })
    },
    bindMqttEvents() {
      if (this.mqttBound) return
      this.mqttMessageHandler = (topic, message) => {
        if (!this.apiDataLoaded) return
        try {
          const payload = JSON.parse(message.toString())
          if (topic.startsWith('live/') && topic.endsWith('/fire alarm')) {
            const deviceList = payload.deviceList
            if (!Array.isArray(deviceList) || !deviceList.length) return

            deviceList.forEach(device => {
              if (device.ignore_status === 1) return

              const deviceId = `${device.block_id}-${device.inverter_room}-${device.fire_alarm_id}`
              if (!this.fireAlarmData[deviceId]) return

              if (this.paramNameMap[deviceId]) {
                Object.keys(this.paramNameMap[deviceId]).forEach(key => {
                  if (!(key in device) && key in this.fireAlarmData[deviceId]) {
                    this.$set(this.fireAlarmData[deviceId], key, null)
                  }
                })
              }

              for (const [key, value] of Object.entries(device)) {
                if (key !== 'name') {
                  this.$set(this.fireAlarmData[deviceId], key, value)
                }
              }

              this._markDeviceLive(deviceId)
            })

            this.mqttStatus = 'connected'
            this.startMqttStaleTimer()
          }
        } catch (e) {
          console.error('[FireAlarm] MQTT parse error:', e)
        }
      }
      this.registerMqttHandlers(this.mqttMessageHandler)
    },
  },
  watch: {
    getSelectedPlantId(newVal, oldVal) {
      this.unsubscribeMqtt(oldVal)
      this.initialStates()
    }
  }
}
</script>

<style scoped>
.legend-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.container-height {
  height: 70vh;
  overflow-y: auto;
}

.mqtt-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 8px;
  margin-bottom: 10px;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.mqtt-banner.offline {
  background: linear-gradient(135deg, rgba(239, 83, 80, 0.15), rgba(239, 83, 80, 0.08));
  color: #EF5350;
  border: 1px solid rgba(239, 83, 80, 0.3);
}

.mqtt-banner.stale {
  background: linear-gradient(135deg, rgba(255, 183, 77, 0.15), rgba(255, 183, 77, 0.08));
  color: #FFB74D;
  border: 1px solid rgba(255, 183, 77, 0.3);
}

.mqtt-banner.reconnecting {
  background: linear-gradient(135deg, rgba(66, 165, 245, 0.15), rgba(66, 165, 245, 0.08));
  color: #42A5F5;
  border: 1px solid rgba(66, 165, 245, 0.3);
}

.banner-icon {
  font-size: 16px !important;
  opacity: 0.9;
}

.banner-text {
  flex: 1;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.offline {
  background: #EF5350;
  box-shadow: 0 0 6px rgba(239, 83, 80, 0.6);
}

.status-dot.stale {
  background: #FFB74D;
  box-shadow: 0 0 6px rgba(255, 183, 77, 0.6);
  animation: pulse-stale 2s ease-in-out infinite;
}

.status-dot.reconnecting {
  background: #42A5F5;
  box-shadow: 0 0 6px rgba(66, 165, 245, 0.6);
  animation: pulse-reconnect 1s ease-in-out infinite;
}

/* ── Live indicator (when connected) ─────────────── */
.live-indicator {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  padding: 4px 12px;
  margin-bottom: 6px;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4CAF50;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.6);
  animation: pulse-live 2s ease-in-out infinite;
}

.live-text {
  font-size: 11px;
  font-weight: 600;
  color: #4CAF50;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ── Animations ──────────────────────────────────── */
@keyframes pulse-live {
  0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(76, 175, 80, 0.6); }
  50% { opacity: 0.6; box-shadow: 0 0 4px rgba(76, 175, 80, 0.3); }
}

@keyframes pulse-stale {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

@keyframes pulse-reconnect {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.3); opacity: 0.7; }
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ── Transitions ─────────────────────────────────── */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}
.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}
.slide-fade-enter {
  transform: translateY(-10px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-5px);
  opacity: 0;
}
</style>
